import { AfterViewInit, ChangeDetectorRef, Component, NgZone, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import dayjs, { Dayjs } from 'dayjs';
import { CERTN_COUNTRIES } from 'src/app/shared/models/provinces';
import { RegistrationService } from '../../shared/registration.service';
import { Location } from '@angular/common';
import { ResidenceAddress } from '../../shared/models/residence-adress.model';
import { catchError } from 'rxjs/operators';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { of } from 'rxjs';
import { CANADA_PROVINCES, USA_PROVINCES } from 'src/app/shared/models/provinces';
const REQUIRED_FIELD_MESSAGE = 'You must enter a value';
const MaximumLengthFieldMessage = (value: number) => `Must be less than ${value} characters`;
const REGEX_LETTERS_SPACES_HYPHENS = '^[A-Za-z- ]*$';
const PATTERN_ERROR_MESSAGE = 'Only letters, spaces and hyphens are acceptable';
const country_US = 'US';
const country_CA = 'CA';
class AddressModel {
  fullAddress: string;
  streetAddress: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
}
@Component({
    selector: 'app-residence-history-form',
    templateUrl: './residence-history-form.component.html',
    styleUrls: ['./residence-history-form.component.scss', '../residence-history.component.scss'],
    standalone: false
})
export class ResidenceHistoryFormComponent implements OnInit, AfterViewInit {
  turnOffAddressLookup: boolean = false;
  lastDateOfMonth = dayjs()
    .endOf('month')
    .valueOf();
  currentDate = new Date(this.lastDateOfMonth);
  maxDate = dayjs(this.currentDate);
  minDate = dayjs(this.currentDate).subtract(30, 'year');
  maxFromDate = this.maxDate;
  minFromDate = this.minDate;
  previousAddressForm;
  certnCountries = CERTN_COUNTRIES;
  countryUSorCA: string[] = [country_US, country_CA];
  countries;
  addresses;
  isContentReady = true;
  isGoogleLookupReinitPending: boolean = false; // Track reinitialization
  canadaProvinces = CANADA_PROVINCES;
  usaProvinces = USA_PROVINCES;
  provinces = [];
  
  // Month and year options for dropdowns
  months = [
    { value: 0, name: 'January' },
    { value: 1, name: 'February' },
    { value: 2, name: 'March' },
    { value: 3, name: 'April' },
    { value: 4, name: 'May' },
    { value: 5, name: 'June' },
    { value: 6, name: 'July' },
    { value: 7, name: 'August' },
    { value: 8, name: 'September' },
    { value: 9, name: 'October' },
    { value: 10, name: 'November' },
    { value: 11, name: 'December' }
  ];
  
  // Generate years from 30 years ago to current year (no future years)
  years: number[] = (() => {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 30;
    const yearsArray: number[] = [];
    for (let year = currentYear; year >= startYear; year--) {
      yearsArray.push(year);
    }
    return yearsArray;
  })();

  @ViewChild('previousAddressText', { static: false }) previousAddressText: any;
  constructor(
    private readonly fb: UntypedFormBuilder,
    private readonly zone: NgZone,
    private readonly registrationService: RegistrationService,
    private readonly location: Location,
    private toastService: ToastService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.previousAddressForm = this.createPreviousAddressForm();
    this.certnCountries = this.certnCountries.map(country => {
      if (!this.countryUSorCA.includes(country.value)) {
        country.value = country.key;
      } else if (country.value === country_US) {
        country.key = 'USA';
      }
      return country;
    }).filter((country, index, self) =>
      index === self.findIndex(c => c.value === country.value)
    );
    this.countries = this.certnCountries;
    this.loadAddresses();
  }
  ngAfterViewInit(): void {
    // Check if the input element is available before initializing autocomplete
    if (!this.previousAddressText?.nativeElement) {
      this.isGoogleLookupReinitPending = true; // Schedule reinitialization
    } else {
      this.loadGoogleAutoComplete();
    }
  }
  ngAfterViewChecked() {
    if (this.isGoogleLookupReinitPending && !this.turnOffAddressLookup) {
      // Ensure the input element is now available
      if (this.previousAddressText?.nativeElement) {
        this.getPlaceAutocomplete(this.previousAddressText);
        this.isGoogleLookupReinitPending = false; // Reset the flag
      }
    }
  }
  loadAddresses() {
    const allAddresses = this.registrationService.getAddressesFromStore();
    if (!allAddresses?.length) {
      this.navigateToListPage();
      return;
    }
    this.addresses = [...allAddresses]?.sort(
      (a, b) => new Date(b.livedFromDate).getTime() - new Date(a.livedFromDate).getTime()
    );
    
    // Find the earliest address (last in sorted array) to set TO date
    // This ensures the new previous address connects to the earliest existing address
    const earliestAddress = this.addresses[this.addresses.length - 1];
    
    if (earliestAddress?.livedFromDate) {
      // Pre-populate TO date with the last day of the month before the earliest address starts
      // This creates a seamless connection without gaps
      const defaultToDate = dayjs(earliestAddress.livedFromDate).subtract(1, 'month').endOf('month');
      
      this.previousAddressToDate.setValue(defaultToDate);
      
      // Set the month/year dropdowns with a slight delay to ensure form is ready
      setTimeout(() => {
        this.previousAddressForm.patchValue({
          previousToMonth: defaultToDate.month(),
          previousToYear: defaultToDate.year()
        });
      }, 100);
    }
  }
  get fullAddress() {
    return this.previousAddressForm.get('fullAddress');
  }
  get previousAddress() {
    return this.previousAddressForm.get('previousAddress');
  }
  get previousAddressCity() {
    return this.previousAddressForm.get('previousAddressCity');
  }
  get previousAddressState() {
    return this.previousAddressForm.get('previousAddressState');
  }
  get previousAddressCountry() {
    return this.previousAddressForm.get('previousAddressCountry');
  }
  get previousAddressZipCode() {
    return this.previousAddressForm.get('previousAddressZipCode');
  }
  get previousAddressFromDate() {
    return this.previousAddressForm.get('previousAddressFromDate');
  }
  get previousAddressToDate() {
    return this.previousAddressForm.get('previousAddressToDate');
  }
  onTurnOffAddressAutoLookup() {
    this.turnOffAddressLookup = true;
    this.fullAddress.clearValidators();
    this.fullAddress.updateValueAndValidity();
    this.previousAddress.setValidators([Validators.required]);
    this.previousAddressCity.setValidators([
      Validators.required,
      Validators.pattern("^[A-Za-z-'. ]*$"),
      Validators.maxLength(25)
    ]);
    this.previousAddressState.setValidators([
      Validators.required,
      Validators.pattern(REGEX_LETTERS_SPACES_HYPHENS),
      Validators.maxLength(25)
    ]);
    this.previousAddressCountry.setValidators([Validators.required]);
    this.previousAddressZipCode.setValidators([Validators.required]);
    this.previousAddressForm.patchValue({
      fullAddress: ''
    });
    const input = this.previousAddressText?.nativeElement; // Check if addressText is available
      if (input?.autocompleteInstance) {
        google.maps.event.clearInstanceListeners(input);
        delete input.autocompleteInstance;
      }
  }
  onTurnOnAddressAutoLookup() {
    this.turnOffAddressLookup = false;
  
    // Reset validators for manual address fields
    this.previousAddress.clearValidators();
    this.previousAddressCity.clearValidators();
    this.previousAddressState.clearValidators();
    this.previousAddressCountry.clearValidators();
    this.previousAddressZipCode.clearValidators();
  
    // Add validators for fullAddress
    this.fullAddress.setValidators([Validators.required]);
    this.fullAddress.updateValueAndValidity();
  
    // Reset manual address fields
    this.previousAddressForm.patchValue({
      previousAddress: '',
      previousAddressCity: '',
      previousAddressState: '',
      previousAddressCountry: '',
      previousAddressZipCode: '',
    });
    
    if (!this.previousAddressText?.nativeElement) {
      this.isGoogleLookupReinitPending = true; // Schedule reinitialization
    } else {
      this.getPlaceAutocomplete(this.previousAddressText);
    }
  }

  onAddressLookupCheckboxToggle(event: any): void {
    if (event?.checked) {
      this.onTurnOffAddressAutoLookup();
      return;
    }
    this.onTurnOnAddressAutoLookup();
  }
  getFullAddressErrorMessage(): string {
    return this.fullAddress.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressErrorMessage(): string {
    return this.previousAddress.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressCityErrorMessage(): string {
    const errors = this.previousAddressCity.errors;
    return errors.required
      ? REQUIRED_FIELD_MESSAGE
      : errors.pattern
      ? PATTERN_ERROR_MESSAGE
      : errors.maxlength
      ? MaximumLengthFieldMessage(errors.maxlength.requiredLength)
      : '';
  }
  getPreviousAddressStateErrorMessage(): string {
    const errors = this.previousAddressState.errors;
    return errors.required
      ? REQUIRED_FIELD_MESSAGE
      : errors.pattern
      ? PATTERN_ERROR_MESSAGE
      : errors.maxlength
      ? MaximumLengthFieldMessage(errors.maxlength.requiredLength)
      : '';
  }
  getPreviousAddressCountryErrorMessage(): string {
    const errors = this.previousAddressCountry.errors;
    return errors.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressZipCodeErrorMessage(): string {
    return this.previousAddressZipCode.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressFromDate(): string {
    return this.previousAddressFromDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  getPreviousAddressToDate(): string {
    return this.previousAddressToDate.errors?.required ? REQUIRED_FIELD_MESSAGE : '';
  }
  onDatePickerClick(datepicker: any) {
    datepicker.open();
  }
  chosenMonthHandler(normalizedMonth: Dayjs, datepicker: MatDatepicker<Dayjs>, element?: string): void {
    const date = dayjs()
      .set('month', normalizedMonth.get('month'))
      .set('year', normalizedMonth.get('year'))
      .set('date', 1);
    const formControl = new UntypedFormControl(date);
    if (element) {
      if (element == 'previous') {
        this.previousAddressForm.patchValue({
          previousAddressFromDate: formControl.value
        });
      }
      if (element == 'toDateControl') {
        this.previousAddressForm.patchValue({
          previousAddressToDate: formControl.value
        });
      }

      // if (element == 'fromCurrentAddressPicker') {
      //   this.form.patchValue({
      //     fromDate: formControl.value
      //   });
      // }

      // if (element == 'currentToDatePicker') {
      //   this.form.patchValue({
      //     toDate: formControl.value
      //   });
      // }
    }
    datepicker.close();
  }
  onCertnCountrySearch(event: any): void {
    const searchText = event?.target?.value?.trim();

    let countries = this.certnCountries;
    if (searchText) {
      this.countries = countries.filter(country => country.key.toLowerCase().includes(searchText.toLowerCase()));
    } else {
      this.countries = countries;
    }
  }
  goBack(): void {
    this.location.back();
  }

  onCancel() {
    this.turnOffAddressLookup = false;
    this.resetPreviousAddressValidation();
  }
  resetPreviousAddressValidation() {
    this.fullAddress.setValidators([Validators.required]);
    this.previousAddress.clearValidators();
    this.previousAddressCity.clearValidators();
    this.previousAddressState.clearValidators();
    this.previousAddressCountry.clearValidators();
    this.previousAddressZipCode.clearValidators();
  }
  onSubmit() {
    // Convert month/year dropdowns to date fields before validation
    this.updateDateFieldsFromMonthYear();
    
    const model = this.preparePreviousAddressModel();
    this.isContentReady = false;

    this.registrationService
      .addAddress(model)
      .pipe(
        catchError((error: any) => {
          this.isContentReady = true;
          this.cdr.detectChanges(); // Manually trigger change detection
          this.toastService.showError(error?.message);
          this.navigateToListPage();
          return of(false);
        })
      )
      .subscribe(res => {
        if (res) {
          this.isContentReady = true;
          this.cdr.detectChanges(); // Manually trigger change detection
          this.toastService.showSuccess('Address added successfully');
          this.navigateToListPage();
        }
      });
  }
  navigateToListPage() {
    this.location.back();
  }
  countryLowerCaseCheck(country: string): string {
    return this.countryUSorCA.includes(country) ? country.toLowerCase() : country;
  }
  private preparePreviousAddressModel(): ResidenceAddress {
    const formModel = this.previousAddressForm.value;
    const addressModel = this.parseAddress(formModel.fullAddress);
    const model: ResidenceAddress = {
      completeAddress: this.turnOffAddressLookup
        ? `${formModel.previousAddress}, ${formModel.previousAddressCity}, ${formModel.previousAddressState} ${formModel.previousAddressZipCode}, ${formModel.previousAddressCountry}`
        : formModel.fullAddress,
      streetAddress: this.turnOffAddressLookup ? formModel.previousAddress : addressModel.streetAddress,
      city: this.turnOffAddressLookup ? formModel.previousAddressCity : addressModel.city,
      province: this.turnOffAddressLookup ? formModel.previousAddressState : addressModel.state,
      country: this.countryLowerCaseCheck(
        this.turnOffAddressLookup ? formModel.previousAddressCountry : addressModel.country
      ),
      postalCode: this.turnOffAddressLookup ? formModel.previousAddressZipCode : addressModel.zipcode,
      livedFromDate: formModel.previousAddressFromDate
        ? (dayjs(formModel.previousAddressFromDate).format('YYYY-MM-DD') as string)
        : '',
      livedToDate: formModel.previousAddressToDate
        ? (dayjs(formModel.previousAddressToDate).format('YYYY-MM-DD') as string)
        : ''
    };
    if (formModel.id && formModel.id != '') {
      model.id = formModel.id;
    }
    return model;
  }
  private parseAddress(address: string): AddressModel {
    const parsedAddressArr = address?.split(',');
    const addressModel = new AddressModel();
    if (address && parsedAddressArr.length === 4) {
      addressModel.fullAddress = address;
      addressModel.streetAddress = parsedAddressArr[0]?.trim();
      addressModel.city = parsedAddressArr[1]?.trim();
      addressModel.state = parsedAddressArr[2]?.trim();
      addressModel.zipcode = addressModel.state.substring(3, addressModel.state.length)?.trim();
      addressModel.state = addressModel.state?.split(' ')[0];
      addressModel.country = parsedAddressArr[3]?.trim();
      if (addressModel.country == 'Canada' || addressModel.country == 'CA') {
        addressModel.country = 'CA';
      }
      if (addressModel.country == 'USA' || addressModel.country == 'US') {
        addressModel.country = 'US';
        addressModel.state = `US-${addressModel.state}`;
      }
    }
    return addressModel;
  }
  private getPlaceAutocomplete(addressText: any): void {
    if (!addressText) {
      return;
    }

    const input = addressText?.nativeElement || addressText;
    
    if (!input) {
      return;
    }

    // Clean up existing autocomplete instance if it exists
    if (input.autocompleteInstance) {
      google.maps.event.clearInstanceListeners(input);
      input.autocompleteInstance = null;
    }

    try {
      const autocomplete = new google.maps.places.Autocomplete(input, {
        componentRestrictions: {
          country: this.countryUSorCA
        },
        types: [],
        fields: ['formatted_address', 'address_components', 'geometry']
      });

      // Set fields explicitly to reduce payload and improve mobile performance
      autocomplete.setFields(['formatted_address', 'address_components', 'geometry']);

      google.maps.event.addListener(autocomplete, 'place_changed', () => {
        const place = autocomplete.getPlace();
        
        // Check if place has required data
        if (place && place.formatted_address) {
          this.zone.run(() => {
            try {
              this.fullAddress.setValue(place.formatted_address);
            } catch (error) {
              console.error('Error processing place selection:', error);
            }
          });
        }
      });

      // Mobile-specific: Ensure autocomplete works well on mobile
      // Note: Removed focus event trigger to prevent Google Maps API errors

      // Store autocomplete instance in the element for later removal
      input.autocompleteInstance = autocomplete;
    } catch (error) {
      console.error('Error initializing Google Places Autocomplete:', error);
    }
  }
  private createPreviousAddressForm(): UntypedFormGroup {
    const form = this.fb.group({
      fullAddress: ['', Validators.required],
      previousAddress: [''],
      previousAddressCity: [''],
      previousAddressState: [''],
      previousAddressCountry: [''],
      previousAddressZipCode: [''],
      // New month/year controls
      previousFromMonth: ['', Validators.required],
      previousFromYear: ['', Validators.required],
      previousToMonth: ['', Validators.required],
      previousToYear: ['', Validators.required],
      // Keep legacy date fields for compatibility
      previousAddressFromDate: ['', Validators.required],
      previousAddressToDate: ['', Validators.required],
      // Add ID field for editing support
      id: ['']
    });

    // Subscribe to month/year changes to update date fields and validate chronological order
    form.get('previousFromMonth')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('previousFromYear')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('previousToMonth')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());
    form.get('previousToYear')?.valueChanges.subscribe(() => this.updateDateFieldsFromMonthYear());

    return form;
  }

  // Helper method to convert month/year to date fields
  private updateDateFieldsFromMonthYear(): void {
    const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const toMonth = this.previousAddressForm.get('previousToMonth')?.value;
    const toYear = this.previousAddressForm.get('previousToYear')?.value;

    if (fromMonth !== null && fromMonth !== '' && fromYear) {
      const fromDate = dayjs().year(fromYear).month(fromMonth).startOf('month');
      this.previousAddressForm.patchValue({ previousAddressFromDate: fromDate.toDate() }, { emitEvent: false });
    }

    if (toMonth !== null && toMonth !== '' && toYear) {
      const toDate = dayjs().year(toYear).month(toMonth).endOf('month');
      this.previousAddressForm.patchValue({ previousAddressToDate: toDate.toDate() }, { emitEvent: false });
    }
  }

  // Get available months for TO month dropdown based on FROM date selection
  getAvailableToMonths(): any[] {
    const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const toYear = this.previousAddressForm.get('previousToYear')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    if (!toYear) return this.months;

    let availableMonths = this.months;

    // Apply chronological order within this address (FROM <= TO)
    if (fromYear && toYear && fromMonth !== null && fromMonth !== '') {
      // If same year, TO month must be >= FROM month
      if (fromYear === toYear) {
        availableMonths = availableMonths.filter(month => month.value >= fromMonth);
      }
    }

    // Apply constraint based on maximum allowed date
    if (maxAllowedDate && toYear) {
      // If TO year is the same as max allowed year, limit months
      if (toYear === maxAllowedDate.year()) {
        availableMonths = availableMonths.filter(month => month.value <= maxAllowedDate.month());
      }
      // If TO year is after max allowed year, no months should be available
      else if (toYear > maxAllowedDate.year()) {
        availableMonths = [];
      }
    }

    // Filter out occupied months (but allow the current address range being edited)
    availableMonths = availableMonths.filter(month => {
      return !this.isMonthYearOccupied(month.value, toYear, editingAddressId);
    });
    
    return availableMonths;
  }

  // Get available years for TO year dropdown based on FROM year selection
  getAvailableToYears(): number[] {
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    const editingAddressId = this.previousAddressForm.get('id')?.value;
    
    let availableYears = this.years;
    
    // Limit by FROM year (chronological order within this address)
    if (fromYear) {
      availableYears = availableYears.filter(year => year >= fromYear);
    }
    
    // Limit by existing addresses (no overlaps)
    if (maxAllowedDate) {
      availableYears = availableYears.filter(year => year <= maxAllowedDate.year());
    }
    
    // Filter out years that have at least one available month for TO date
    return availableYears.filter(year => {
      // Check if this year has any available months for TO date
      const availableMonthsInYear = this.months.filter(month => {
        // Apply FROM month constraint if same year
        if (fromYear && year === fromYear) {
          const fromMonth = this.previousAddressForm.get('previousFromMonth')?.value;
          if (fromMonth !== null && fromMonth !== '' && month.value < fromMonth) {
            return false;
          }
        }
        
        // Apply max allowed date constraint
        if (maxAllowedDate && year === maxAllowedDate.year() && month.value > maxAllowedDate.month()) {
          return false;
        }
        
        return !this.isMonthYearOccupied(month.value, year, editingAddressId);
      });
      
      return availableMonthsInYear.length > 0;
    });
  }

  // Get available years for FROM year dropdown - exclude years already fully covered by existing addresses
  getAvailableFromYears(): number[] {
    const editingAddressId = this.previousAddressForm.get('id')?.value;
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    
    let availableYears = this.years;
    
    // Filter by maximum allowed date first
    if (maxAllowedDate) {
      availableYears = availableYears.filter(year => year <= maxAllowedDate.year());
    }
    
    // Filter out years that have at least one available month
    return availableYears.filter(year => {
      // Check if this year has any available months
      const availableMonthsInYear = this.months.filter(month => {
        return !this.isMonthYearOccupied(month.value, year, editingAddressId);
      });
      
      return availableMonthsInYear.length > 0;
    });
  }

  // Helper method to check if a specific month/year is occupied by existing addresses
  private isMonthYearOccupied(month: number, year: number, excludeAddressId?: string): boolean {
    if (!this.addresses?.length) return false;

    return this.addresses.some(addr => {
      // Skip the address being edited
      if (excludeAddressId && addr.id === excludeAddressId) return false;
      
      if (!addr.livedFromDate) return false;

      const addrFromDate = dayjs(addr.livedFromDate);
      const addrToDate = addr.livedToDate ? dayjs(addr.livedToDate) : dayjs(); // Current date if no end date
      
      const checkDate = dayjs().year(year).month(month).date(1);
      
      // Check if the month/year falls within the address period (inclusive)
      // Using basic comparison instead of isSameOrAfter/isSameOrBefore
      const checkTimestamp = checkDate.valueOf();
      const fromTimestamp = addrFromDate.startOf('month').valueOf();
      const toTimestamp = addrToDate.endOf('month').valueOf();
      
      return checkTimestamp >= fromTimestamp && checkTimestamp <= toTimestamp;
    });
  }

  // Get available months for FROM month dropdown - exclude months already covered by existing addresses
  getAvailableFromMonths(): any[] {
    const fromYear = this.previousAddressForm.get('previousFromYear')?.value;
    const editingAddressId = this.previousAddressForm.get('id')?.value;
    
    if (!fromYear) return this.months;

    // Filter out occupied months
    return this.months.filter(month => {
      return !this.isMonthYearOccupied(month.value, fromYear, editingAddressId);
    });
  }

  // Helper method to get the maximum allowed date for a previous address
  private getMaxAllowedDateForPreviousAddress(): dayjs.Dayjs | null {
    if (!this.addresses || this.addresses.length === 0) {
      return null;
    }

    // Get the ID of the address being edited (if any) to exclude it from constraints
    const editingAddressId = this.previousAddressForm.get('id')?.value;

    // Find the current address
    const currentAddress = this.addresses.find(addr => addr.isCurrentAddress === true);
    
    if (currentAddress && currentAddress.livedFromDate) {
      // Previous address must end in the month before current address starts (intuitive date range)
      return dayjs(currentAddress.livedFromDate).subtract(1, 'month');
    }

    // If no current address, find the earliest existing address (excluding the one being edited)
    const sortedAddresses = this.addresses
      .filter(addr => addr.livedFromDate && addr.id !== editingAddressId)
      .sort((a, b) => new Date(a.livedFromDate).getTime() - new Date(b.livedFromDate).getTime());
    
    if (sortedAddresses.length > 0) {
      // Previous address must end in the month before the next address starts
      return dayjs(sortedAddresses[0].livedFromDate).subtract(1, 'month');
    }

    return null;
  }

  // Helper method to get constraint message for user feedback
  getDateConstraintMessage(): string {
    const maxAllowedDate = this.getMaxAllowedDateForPreviousAddress();
    
    if (maxAllowedDate) {
      const currentAddress = this.addresses.find(addr => addr.isCurrentAddress === true);
      
      if (currentAddress) {
        const currentFromDate = dayjs(currentAddress.livedFromDate).format('MMMM YYYY');
        const maxAllowedMonth = dayjs(currentAddress.livedFromDate).subtract(1, 'month').format('MMMM YYYY');
        return `Previous addresses must end by ${maxAllowedMonth} (the month before your current address started in ${currentFromDate}). The TO date has been set to avoid gaps in your timeline.`;
      } else {
        const mostRecentAddress = this.addresses[0];
        if (mostRecentAddress?.livedFromDate) {
          const recentFromDate = dayjs(mostRecentAddress.livedFromDate).subtract(1, 'month').format('MMMM YYYY');
          return `The TO date has been set to ${recentFromDate} to connect with your most recent address. You can adjust it if needed.`;
        }
        return `Previous addresses must not overlap with existing addresses`;
      }
    }
    
    return '';
  }
  private loadGoogleAutoComplete(): void {
    this.getPlaceAutocomplete(this.previousAddressText);
  }
  onCountryChange(): void {
    const selectedCountry = this.previousAddressForm.get('previousAddressCountry').value;

    if (selectedCountry === 'CA') {
      this.provinces = this.canadaProvinces;
    } else if (selectedCountry === 'US') {
      this.provinces = this.usaProvinces;
    } else {
      this.provinces = [];
    }

    // Reset the state/province field when the country changes
    this.previousAddressForm.get('previousAddressState').setValue('');
  }
}
